/* eslint-disable no-unused-vars */
/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-toast.html";

import "@nuskin/ns-style-elements";

/**
 * <p>The ns-toast pops us a short, temporary notice to the user. It comes in 3 flavors: CTA, Fleeting and Persistent.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/307183654/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/307183654/1/latest/pORgewzvT4DqSqlEbolEmv02uxqJi4bsbYvClEkIfRc8EVt06cFK2lS2ylEdr5x4WrBWhsyW7tGbIbPFHlELQxpPXlEQlE/Toasts-2x.png" alt="Buttons UX Documentation">
 * </a>
 * <br>
 * <ns-button href="#elements-app/elements/NsToast" onclick="window.location='/playground/ns-toast-demo.html';">Ns Toast Playground Demo</ns-button>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <style>
 *       ns-toast {
 *         position: relative; //This is just so they won't all stack at the bottom of the screen
 *       }
 *     </style>
 *     <ns-toast opened type="cta"><p>Default Toast</p><a slot="cta">Example CTA</a></ns-toast>
 *     <br>
 *     <ns-toast opened theme="information">Information Toast</ns-toast>
 *     <br>
 *     <ns-toast opened type="fleeting" theme="error">Error Toast</ns-toast>
 *     <br>
 *     <ns-toast opened type="cta" theme="warning"><p>Warning Toast</p><a href="nuskin.com" slot="cta">click this thing</a></ns-toast>
 *     <br>
 *     <ns-toast opened theme="success" icon="nuskin:icon-edit">Success Toast</ns-toast>
 *     <br>
 *     <ns-toast opened type="persistent" theme="information" no-icon>Long Toast to see what happens when you put too much text in a toast message. It should wrap.</ns-toast>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      `<ns-toast>` currently provides no custom properties and mixins for styling.
 *
 *      ### Slots
 *
 *      ##### `<ns-toast>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      --------------------------------|---------------------------------------|-------------------
 *      `default`                       | Content of the toast                       | `'Sample Toast'`
 *      `cta`                           | Content for the CTA on the right side      | `''`
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsToast extends PolymerElement {
  constructor() {
    super();
  }

  static get is() {
    return "ns-toast";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * How long fleeting toasts should stay on the screen
       */
      duration: {
        type: Number,
        value: 3200
      },
      /**
       * The icon that should be displayed on the left of the toast. Takes a string representing the set and id of the icon
       */
      icon: {
        type: String,
        value: "nuskin:icon-info-filled"
      },
      /**
       * Hide the icon displayed on the left of the toast
       */
      noIcon: {
        type: Boolean
      },
      /**
       * Indicated the opened or closed state of the toast. You can also set the attribute to open the toast.
       */
      opened: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Determines how the toast should look. Options include: none (default), 'information', 'success', 'warning', 'error'
       */
      theme: {
        type: String,
        value: "fleeting"
      },
      /**
       * Determines the type of toast. Options include: 'fleeting' (default), 'persistent', 'cta'
       */
      type: {
        type: String
      },
      showCloseIcon: {
        type: Boolean
      }
    };
  }

  ready() {
    super.ready();
    // need to close the toast if the cta is clicked
    this.$.ctaWrapper.addEventListener("click", (e) => {
      if (this.type === "cta") {
        this.hide();
      }
    });
    this.showCloseIcon = this.hasAttribute("showCloseIcon");
    if (!this.hasAttribute("icon")) {
      switch (this.theme) {
        case "information":
          this.icon = "nuskin-color:icon-info-filled";
          break;
        case "success":
          this.icon = "nuskin-color:icon-success-filled";
          break;
        case "warning":
          this.icon = "nuskin-color:icon-warning-filled";
          break;
        case "error":
          this.icon = "nuskin-color:icon-error-filled";
          break;

        default:
          break;
      }
    }
  }

  /**
   * Display the toast
   */
  show() {
    this.opened = true;
    if (this.type === "fleeting" || !this.type) {
      setTimeout(() => {
        this.hide();
      }, this.duration);
    }
  }

  /**
   * Hide the toast
   */
  hide() {
    this.opened = false;
  }

  /**
   * Facade method for show()
   */
  open() {
    this.show();
  }

  /**
   * Facade method for hide()
   */
  close() {
    this.hide();
  }

  /**
   * Toggle the display of the toast
   */
  toggle() {
    this.opened ? this.hide() : this.show();
  }
}

customElements.define(NsToast.is, NsToast);
